// src/lib/media/schema.ts
//
// Pure, isomorphic builders: MediaMeta -> schema.org JSON-LD nodes, plus the
// YouTube id/URL helpers. NO fs, NO process.env — safe to import in components
// that render on the client (e.g. MetaHead.generateFullPageMetadata). All
// filesystem/registry resolution lives in resolve.ts (server only); this module
// only shapes already-resolved data.

import type { MediaMeta } from './types';

// ─── mime ──────────────────────────────────────────────────────────────────

const MIME: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  avif: 'image/avif', gif: 'image/gif', svg: 'image/svg+xml',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', flv: 'video/x-flv',
  mp3: 'audio/mpeg', wav: 'audio/wav',
  pdf: 'application/pdf',
};

export function mimeFromExt(ext: unknown): string {
  if (typeof ext !== 'string') return 'application/octet-stream';
  const clean = ext.replace(/^\./, '').toLowerCase();
  return MIME[clean] || `application/${clean}`;
}

// ─── youtube ─────────────────────────────────────────────────────────────────

// Pull the 11-char video id out of any youtube url form (watch, embed, youtu.be,
// shorts) or return the input unchanged if it already looks like a bare id.
export function youtubeId(input: string): string | null {
  if (!input) return null;
  const s = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i,
  );
  return m ? m[1] : null;
}

export function youtubeThumb(id: string): string {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

// Pull every distinct YouTube video id out of a blob of page content (markdown
// + raw html: watch links, embeds, youtu.be, shorts, iframes). Order-preserving,
// deduped — the set of videos the page actually references.
export function extractYouTubeIds(content: string): string[] {
  if (!content) return [];
  const re = /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^"'\s]*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/gi;
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(content))) seen.add(m[1]);
  return [...seen];
}

// A MediaMeta for an embedded YouTube video. uploadDate/duration aren't knowable
// without the Data API, so they're left undefined — Google treats them as
// recommended (not required) for VideoObjects that supply embedUrl + thumbnail.
export function youtubeMeta(idOrUrl: string, opts: { title?: string; description?: string } = {}): MediaMeta | null {
  const id = youtubeId(idOrUrl);
  if (!id) return null;
  return {
    kind: 'video',
    id,
    src: `https://www.youtube.com/embed/${id}`,
    pageUrl: `https://www.youtube.com/watch?v=${id}`,
    title: opts.title || 'Video',
    alt: opts.title || '',
    caption: '',
    description: opts.description || opts.title || '',
    keywords: [],
    thumbnailUrl: youtubeThumb(id),
    embedUrl: `https://www.youtube.com/embed/${id}`,
    encodingFormat: 'video/mp4',
    ok: true,
    source: 'youtube',
  };
}

// ─── seconds -> ISO-8601 duration ─────────────────────────────────────────────

export function isoDuration(seconds: unknown): string | undefined {
  const s = typeof seconds === 'string' ? parseFloat(seconds) : (seconds as number);
  if (!Number.isFinite(s) || s <= 0) return undefined;
  const total = Math.round(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}${sec ? `${sec}S` : ''}` || 'PT0S';
}

// ─── absolute url helper ──────────────────────────────────────────────────────

// Resolve a possibly-root-relative src to an absolute url against base.
export function absUrl(src: string | undefined, base: string): string {
  if (!src) return '';
  if (/^https?:\/\//i.test(src)) return src;
  const b = (base || '').replace(/\/+$/, '');
  return `${b}${src.startsWith('/') ? '' : '/'}${src}`;
}

// ─── schema.org node builders ──────────────────────────────────────────────────

function defined<T extends object>(o: T): Partial<T> {
  const out: any = {};
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) continue;
    out[k] = v;
  }
  return out;
}

export function toImageObject(m: MediaMeta, base = ''): object {
  const url = absUrl(m.src, base);
  return {
    '@type': 'ImageObject',
    ...defined({
      name: m.title,
      description: m.description || m.caption,
      caption: m.caption || m.description,
      url,
      contentUrl: url,
      width: m.width,
      height: m.height,
      license: m.license,
      creditText: m.attribution,
      keywords: m.keywords.length ? m.keywords.join(', ') : undefined,
      datePublished: m.datePublished,
      mainEntityOfPage: m.pageUrl ? absUrl(m.pageUrl, base) : undefined,
      encodingFormat: m.encodingFormat,
    }),
  };
}

export function toVideoObject(m: MediaMeta, base = ''): object {
  return {
    '@type': 'VideoObject',
    ...defined({
      name: m.title,
      description: m.description || m.caption || m.title,
      thumbnailUrl: m.thumbnailUrl ? absUrl(m.thumbnailUrl, base) : undefined,
      uploadDate: m.uploadDate || m.datePublished,
      duration: m.duration,
      contentUrl: m.embedUrl ? undefined : absUrl(m.src, base),
      embedUrl: m.embedUrl,
      keywords: m.keywords.length ? m.keywords.join(', ') : undefined,
      transcript: m.transcript,
      encodingFormat: m.encodingFormat,
    }),
  };
}

export function toDigitalDocument(m: MediaMeta, base = ''): object {
  return {
    '@type': 'DigitalDocument',
    ...defined({
      name: m.title,
      description: m.description || m.caption,
      url: absUrl(m.src, base),
      thumbnailUrl: m.thumbnailUrl ? absUrl(m.thumbnailUrl, base) : undefined,
      keywords: m.keywords.length ? m.keywords.join(', ') : undefined,
      encodingFormat: 'application/pdf',
      ...(m.pageCount ? { numberOfPages: m.pageCount } : {}),
    }),
  };
}

// Dispatch to the right node builder by kind.
export function toSchemaNode(m: MediaMeta, base = ''): object {
  if (m.kind === 'video' || m.kind === 'audio') return toVideoObject(m, base);
  if (m.kind === 'pdf') return toDigitalDocument(m, base);
  return toImageObject(m, base);
}
